import { makeAutoObservable, reaction, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../types/ServiceStoreTypes";
import { isOperator } from "../../auth/tier";
import { numericUserId } from "../../auth/userId";
import {
  ENGINEERING_TEAM,
  KIND_LABEL,
  displayName,
  isFeedbackKind,
  isTicketScope,
  legalActions,
  sameTeam,
  teamKey,
  type ActorView,
  type StaffUser,
  type TicketAction,
  type TicketEventRecord,
  type TicketKind,
  type TicketPriority,
  type TicketRecord,
  type TicketScope,
} from "./tickets.types";
import { findOrThrow, swallowedFailure } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { watchTicketChanges } from "./ticketsLive";

/** State behind the New ticket dialog. */
export interface TicketDraft {
  title: string;
  body: string;
  kind: TicketKind;
  priority: TicketPriority;
  /** Exactly one of these is normally set; both is a named person on a team queue. */
  assigneeId: number | null;
  team: string | null;
  dueAt: string | null;
  /** What the ticket is ABOUT (optional): a client, and one of its modules. */
  clientId: string | null;
  /** A module-catalog key (`module_catalog.moduleKey`) — the API refuses an alias. */
  governedSlug: string | null;
}

export interface TeamOption {
  team: string;
  members: number;
}

/** A client the New ticket dialog can say a ticket is about. */
export interface TicketClientOption {
  id: string;
  name: string;
  code: string | null;
}

/** A module the New ticket dialog can say a ticket is about. */
export interface TicketModuleOption {
  /** `module_catalog.moduleKey` — what the API stores as `governedSlug`. */
  key: string;
  name: string;
}

const emptyDraft = (): TicketDraft => ({
  title: "",
  body: "",
  kind: "other",
  priority: "normal",
  assigneeId: null,
  team: null,
  dueAt: null,
  clientId: null,
  governedSlug: null,
});

/**
 * The Tickets desk: the four tabs, one open ticket with its timeline, and the
 * actions the server will accept from this person. Route ViewStore — a plain
 * class, per the web rules.
 *
 * A DELIBERATE DEVIATION from rule 1 ("never store API entities in
 * ViewStores"): `page`, `selected` and `events` hold the server rows for
 * the current scope, search and open ticket. The `tickets` ServiceStore's
 * `entities` map is shared with the Command Center's `$limit: 1` count read,
 * so it cannot be trusted to be "the page this desk asked for" — the same
 * reason CommandCenterStore reads a find() RESULT rather than entities. The
 * rows are treated as a snapshot: every write re-reads.
 *
 * WHAT IT REFUSES TO CONFLATE: "the list is empty", "the list failed to load"
 * and "nothing has loaded yet" all render as a short table, and they mean
 * three different things. `loaded`, `error` and `total` keep them apart.
 */
class TicketDeskStore {
  rootStore: RootStore;

  scope: TicketScope = "mine";
  includeClosed = false;
  /**
   * Only the tickets nobody has picked up (the API's `awaitingPickup`): no
   * acknowledgement, no start, no work linked from them since they were filed
   * or last reopened. A narrowing of the tab, never a widening.
   */
  awaitingPickupOnly = false;
  searchQuery = "";
  loading = false;
  loaded = false;
  error: string | null = null;
  /** The API's total for the current scope — null until a page arrives. */
  total: number | null = null;
  page: TicketRecord[] = [];
  /** The search term the current page was loaded WITH — the server's answer for it. */
  loadedSearch = "";
  /** Whose desk is in this store. A different person means: start empty. */
  private loadedForUserId: number | null = null;
  /** `loadSubjects`' own finds have answered (see there). Reset on sign-out. */
  private subjectsLoaded = { clients: false, modules: false };

  /**
   * Drop everything held for a previous account. A ViewStore outlives a
   * sign-out — the RootStore is not rebuilt — so without this the next person
   * on a shared browser would see the previous person's tickets while their
   * own first read was in flight, and indefinitely if it failed.
   */
  reset() {
    runInAction(() => {
      // Invalidate anything still in flight FIRST: a `get` or timeline read
      // started for the previous person must not land in the new one's drawer,
      // and nothing of theirs still running owns a field of this person's —
      // not the action button, not the submit gate, not the people list.
      this.loadSeq += 1;
      this.openSeq += 1;
      this.actSeq += 1;
      this.submitSeq += 1;
      this.usersSeq += 1;
      this.eventsSeq += 1;
      this.page = [];
      this.total = null;
      this.selected = null;
      this.events = [];
      this.loaded = false;
      this.error = null;
      this.eventsError = null;
      this.actionError = null;
      this.loadedSearch = "";
      // What the previous person typed is theirs as well: a half-written
      // ticket, and a search term that may name someone or something the next
      // person has no business seeing spelled out in their own search box.
      this.searchQuery = "";
      this.draft = null;
      this.commentDraft = "";
      this.assignTo = null;
      this.dueDraft = null;
      this.dueDraftDirty = false;
      this.usersLoaded = false;
      this.usersLoading = false;
      this.usersError = null;
      this.loadedForUserId = null;
      this.subjectsLoaded = { clients: false, modules: false };
      // THE FLAGS TOO, or the next person inherits a spinner that never stops.
      // Every read clears its own flag only while its generation is still
      // current — `load`'s `finally` and `loadEvents`' both test it — and the
      // two increments above have just invalidated all of them. So a sign-out
      // landing mid-read left `loading` (or `eventsLoading`) true with nothing
      // left in flight to turn it off.
      this.loading = false;
      this.eventsLoading = false;
      this.acting = null;
      this.submitting = false;
      // The pause the previous person's typing was waiting out is theirs as
      // well; letting it fire would re-read the list for a search box that has
      // just been emptied.
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    });
  }

  // ---- live (route onEnter / onExit) ----------------------------------------

  /** The live subscription's disposer (plain plumbing). */
  private stopLive: (() => void) | null = null;

  /**
   * Follow other people's changes while the desk is open (#3820). The rows
   * here are a snapshot this store re-reads after its own writes; a ticket
   * another person filed, took, commented on or closed reached nobody's desk
   * until they pressed Refresh. Every ticket event this socket hears is one
   * this person may read (the API sends a ticket only to its readers), so a
   * burst of them re-reads the page through `load()` — its own generation and
   * person guards apply — and, when the open ticket was among them, its
   * timeline. Idempotent; `unwatchLive` is the route's onExit.
   */
  watchLive() {
    if (this.stopLive || this.wiringError) return;
    this.stopLive = watchTicketChanges(
      this.rootStore,
      // Not before the first read is even asked for (it will read the change
      // anyway) — but DURING it, yes: its answer may predate the change, and
      // the re-read this schedules supersedes it or follows it.
      () => this.loaded || this.loading,
      (touched) => {
        void this.load();
        const open = this.selected?.id;
        if (open != null && touched.has(Number(open))) void this.loadEvents(open);
      },
    );
  }

  unwatchLive() {
    this.stopLive?.();
    this.stopLive = null;
  }

  /** The ticket open in the drawer, with its timeline. */
  selected: TicketRecord | null = null;
  events: TicketEventRecord[] = [];
  eventsLoading = false;
  /** A timeline that FAILED to load, kept apart from one that is empty. */
  eventsError: string | null = null;
  commentDraft = "";
  /** Which action is in flight, if any — the buttons disable on it. */
  acting: TicketAction | null = null;
  actionError: string | null = null;
  /** For `assign`: the picked user; for `set-due`: the picked date. */
  assignTo: number | null = null;
  dueDraft: string | null = null;
  /**
   * Has the person typed in the due-date field since it was seeded? A refresh
   * re-seeds the field from the ticket it just replaced — otherwise the drawer
   * shows the new deadline in the summary and the old one in "Needed by", and
   * pressing Set re-sends the stale date — but it must never eat a date the
   * person is in the middle of choosing.
   */
  private dueDraftDirty = false;

  draft: TicketDraft | null = null;
  submitting = false;

  usersLoaded = false;

  private loadSeq = 0;
  /** Guards the drawer the way loadSeq guards the list: last open wins. */
  private openSeq = 0;
  /**
   * One generation per SHARED THING a completion may write, because the
   * drawer's generation cannot speak for any of them.
   *
   * `openSeq` says which ticket is on screen. It says nothing about who owns
   * `acting`, `submitting`, `usersLoaded` or the timeline currently being
   * read — and those are exactly the fields a completion touches on its way
   * out, in a `finally` that runs whatever else has happened since. A cleanup
   * that does not prove it still owns the field it is clearing hands the
   * field to whoever is using it NOW: a stale action re-enabled the button a
   * successor's request was still in flight behind (a duplicate comment, one
   * click later), and a stale submission cleared `submitting` and wiped the
   * draft the current person was in the middle of filing.
   *
   * Each is the same shape: take the generation when you take the field, and
   * clear it only while the generation is still yours. `reset()` advances
   * every one of them, so nothing that outlived a sign-out owns anything.
   */
  private actSeq = 0;
  private submitSeq = 0;
  private usersSeq = 0;
  private eventsSeq = 0;

  /**
   * Set when this desk was built with no auth store to watch, and then never
   * cleared — the desk is disabled for the life of the store.
   *
   * WHY IT IS FATAL RATHER THAN COSMETIC. The cross-account teardown IS the
   * reaction below. `rootStore.authStore` is a plain, non-observable property
   * (RootStore.mountStore assigns it), so if it is absent when this store is
   * constructed the reaction's data function reads no observable at all: MobX
   * has nothing to invalidate it with and never re-runs it. The desk then
   * looks completely healthy and holds one person's tickets for the next, with
   * no symptom to notice. Today the mount order puts authStore first and this
   * cannot happen; a reordering, a lazily-mounted authStore or a differently
   * composed RootStore would reintroduce the whole cross-account defect
   * silently. So it is made loud and fails CLOSED: nothing is read, nothing is
   * shown, and both the screen and the console say why.
   */
  wiringError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const authStore = (rootStore as unknown as { authStore?: unknown })
      ?.authStore;
    if (!authStore) {
      this.wiringError =
        "The ticket desk could not be connected to the signed-in account, so it has been turned off. This is a wiring fault, not a permission one — reload, and report it if it persists.";
      // Rendered by the Body as the list's error, and `loaded` so it reads as
      // an answer rather than a page that is still on its way.
      this.error = this.wiringError;
      this.loaded = true;
    }
    makeAutoObservable(this, { rootStore: false });

    if (this.wiringError) {
      console.error(
        "TicketDeskStore: constructed with no rootStore.authStore. The sign-out / account-switch reaction cannot be armed, so the desk has been disabled rather than left holding one person's tickets for the next. Mount authStore before the app stores.",
      );
      return;
    }

    // EMPTY THE DESK WHEN THE PERSON CHANGES, INCLUDING ON SIGN-OUT. Nothing
    // else will: the RootStore is not rebuilt at sign-out, the route has no
    // onExit, and the library's logout purge walks the SERVICE stores only
    // (RootStore.purgeUserData → getServiceStoreNames), so this ViewStore —
    // which holds its own copies of the rows, deliberately — is never cleared
    // by the framework. The lazy check in load() cannot stand in for this: on
    // the feature's primary entry path (?open=<id> from a bell notice, a push
    // or Gantry) a FAILED list read still resolves and open() paints the
    // drawer, and that path never writes `loadedForUserId`.
    //
    // A METHOD, not the `me` getter: reading a getter here turns it into a
    // computed the reaction keeps alive, which then reports the first user
    // forever.
    reaction(
      () => this.currentUserId(),
      (id, previous) => {
        if (id === previous) return;
        this.reset();
      },
    );
  }

  // ---- services --------------------------------------------------------------

  private get ticketsService(): ServiceStoreType | null {
    return (this.rootStore.ticketsStore as ServiceStoreType | null) ?? null;
  }
  private get eventsService(): ServiceStoreType | null {
    return (
      (this.rootStore.ticketEventsStore as ServiceStoreType | null) ?? null
    );
  }
  private get usersService(): ServiceStoreType | null {
    return (this.rootStore.userStore as ServiceStoreType | null) ?? null;
  }
  private get clientsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("clients") ??
        this.rootStore.clientStore) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get moduleCatalogService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-catalog") ??
        this.rootStore.moduleCatalogStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  /**
   * The library ServiceStore SWALLOWS a transport error into `errorOn<Method>`
   * and resolves the call to null/undefined. A write that "returned nothing"
   * therefore FAILED, and this is where its reason is read.
   */
  private swallowed(
    method: "Create" | "Patch" | "Find" | "Get",
  ): string | null {
    const store = this.ticketsService as unknown as Record<
      string,
      { message?: unknown } | null
    > | null;
    const err = store?.[`errorOn${method}`];
    return err && typeof err.message === "string" ? err.message : null;
  }

  /**
   * Why a `get` came back empty. The API answers an invisible or missing
   * ticket with a 404 (and a refused one with a 403), and the ServiceStore
   * swallows THOSE into `errorOnGet` exactly as it swallows a transport
   * failure — so "there is an error" does not mean "the ticket exists". Only
   * a 404/403, or no error at all, is "not visible or does not exist";
   * anything else is a failure to reach the server, and a bell deep-link
   * during an outage must say so rather than confidently deny the ticket.
   */
  private missingTicketMessage(id: number): string {
    const store = this.ticketsService as unknown as {
      errorOnGet?: { code?: unknown; message?: unknown } | null;
    } | null;
    const err = store?.errorOnGet ?? null;
    const code = typeof err?.code === "number" ? err.code : null;
    if (!err || code === 404 || code === 403)
      return `Ticket TKT-${id} is not visible to you or does not exist.`;
    const why = typeof err.message === "string" ? err.message.trim() : "";
    return why
      ? `Ticket TKT-${id} could not be loaded: ${why}`
      : `Ticket TKT-${id} could not be loaded.`;
  }

  // ---- who am I --------------------------------------------------------------

  /**
   * The signed-in id, read the same way `me` reads it — the reaction above and
   * every in-flight guard below have to agree with `me` about who this desk
   * belongs to, so there is one definition of it.
   */
  private currentUserId(): number | null {
    const id = (
      this.rootStore as unknown as {
        authStore?: { currentUser?: { id?: unknown } };
      }
    ).authStore?.currentUser?.id;
    return numericUserId(id);
  }

  get me(): ActorView | null {
    const user = (
      this.rootStore as unknown as { authStore?: { currentUser?: StaffUser } }
    ).authStore?.currentUser;
    // Parsed the same way `currentUserId` parses it, and for the same reason:
    // during a restored session the id is a string, and `legalActions` compares
    // it with `===` against the ticket's numeric requester/assignee. Left as
    // "5" it matches nothing, and the drawer offers this person none of their
    // own actions until the first server round-trip lands.
    const id = numericUserId(user?.id);
    if (id == null) return null;
    return {
      id,
      team: user?.team ?? null,
      isOperator: isOperator(this.rootStore),
    };
  }

  /** All staff, for the person picker and the team list. */
  get users(): StaffUser[] {
    return (this.usersService?.entities ?? []) as unknown as StaffUser[];
  }

  /**
   * Distinct `users.team` values with member counts — the only teams a ticket
   * may route to.
   *
   * KEYED BY `teamKey`, not by the raw spelling. Grouping is a comparison, and
   * the raw string is not the rule (tickets.types.ts): `users.team` is free
   * text, so "Engineering", "engineering" and "engineering " would otherwise
   * be three teams here, each showing a fraction of the members, while the
   * server treats them as one and routes the ticket to all of them. The
   * counts would then be wrong in the one place a person uses them to decide
   * whom they are asking.
   *
   * The spelling SHOWN is the one that sorts first among the variants, so the
   * label does not depend on the order the user list happens to arrive in;
   * whichever it is, the server resolves it to the same team.
   */
  get teams(): TeamOption[] {
    const byKey = new Map<string, TeamOption>();
    for (const u of this.users) {
      const key = teamKey(u.team);
      if (!key) continue;
      const label = (u.team ?? "").trim();
      const seen = byKey.get(key);
      if (!seen) byKey.set(key, { team: label, members: 1 });
      else {
        seen.members += 1;
        if (label.localeCompare(seen.team) < 0) seen.team = label;
      }
    }
    return [...byKey.values()].sort((a, b) => a.team.localeCompare(b.team));
  }

  /**
   * Clients a ticket can be about, A→Z, read from the shared `clients`
   * ServiceStore cache (filled by `loadSubjects`). A removed client is not
   * offered: the API refuses to file about one.
   */
  get clients(): TicketClientOption[] {
    const rows = (this.clientsService?.entities ?? []) as unknown as Array<{
      id?: unknown;
      name?: unknown;
      code?: unknown;
      deletedAt?: unknown;
    }>;
    return rows
      .filter((c) => typeof c.id === "string" && c.id && !c.deletedAt)
      .map((c) => ({
        id: c.id as string,
        name: typeof c.name === "string" && c.name.trim() ? c.name.trim() : (c.id as string),
        code: typeof c.code === "string" && c.code ? c.code : null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Modules a ticket can be about: the catalog's ASSIGNABLE rows. The API
   * stores a module only by its catalog key and refuses a platform row (core,
   * the libraries) — offering one would invite a refusal.
   */
  get modules(): TicketModuleOption[] {
    const rows = (this.moduleCatalogService?.entities ?? []) as unknown as Array<{
      moduleKey?: unknown;
      displayName?: unknown;
      assignable?: unknown;
      deletedAt?: unknown;
    }>;
    const seen = new Set<string>();
    const out: TicketModuleOption[] = [];
    for (const m of rows) {
      if (typeof m.moduleKey !== "string" || !m.moduleKey.trim()) continue;
      if (m.assignable === false || m.deletedAt) continue;
      const key = m.moduleKey.trim();
      if (seen.has(key.toLowerCase())) continue;
      seen.add(key.toLowerCase());
      const name =
        typeof m.displayName === "string" && m.displayName.trim() ? m.displayName.trim() : key;
      out.push({ key, name });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * The client a ticket is about, as the list shows it: the client's name when
   * the clients cache knows it, else the code the API resolved on the way out,
   * else null (not about a client).
   */
  clientLabel(t: Pick<TicketRecord, "clientId" | "clientCode">): string | null {
    if (!t.clientId) return null;
    const known = this.clients.find((c) => c.id === t.clientId);
    return known?.name ?? t.clientCode ?? null;
  }

  /** The open ticket's id — the desk list highlights its row (TicketTable). */
  get selectedId(): number | null {
    return this.selected?.id ?? null;
  }

  /**
   * Fill the clients and module-catalog caches the New ticket dialog's two
   * "about" pickers read. Best-effort: a failure leaves a picker empty, and a
   * ticket is still fileable without either — they are optional context.
   */
  async loadSubjects() {
    const ended = sessionMark(this.rootStore);
    // Latched on THIS method's own find having succeeded — never on the shared
    // cache holding rows. `clients` and `module-catalog` are ServiceStores
    // other screens fill too: Client 360's `clientsStore.get(id)` leaves ONE
    // client in the cache, and "the cache is not empty" then skipped the find
    // for good, so the client picker offered only that client.
    const quietly = async (
      key: keyof typeof this.subjectsLoaded,
      service: ServiceStoreType | null,
      query: Record<string, unknown>,
    ) => {
      if (!service || this.subjectsLoaded[key]) return;
      try {
        // findOrThrow: the ServiceStore swallows a failed find, which must not
        // latch the picker empty.
        await findOrThrow(service, { query });
        if (ended()) return;
        runInAction(() => {
          this.subjectsLoaded[key] = true;
        });
      } catch {
        // Optional context; the dialog still files. Not latched: the next
        // opening of the dialog tries again.
      }
    };
    await Promise.all([
      quietly("clients", this.clientsService, { $limit: 500, $sort: { name: 1 } }),
      quietly("modules", this.moduleCatalogService, { $limit: 200, $sort: { displayName: 1 } }),
    ]);
  }

  userName(id: number | null | undefined): string {
    if (id == null) return "";
    return displayName(this.users.find((u) => u.id === id)) || `#${id}`;
  }

  // ---- list ------------------------------------------------------------------

  setScope(scope: TicketScope) {
    if (this.scope === scope) return;
    this.switchScope(scope);
    void this.load();
  }

  /**
   * Open the desk on the tab a link names (`?scope=all`, read by the route's `onEnter`). It does NOT
   * load: the route's own load follows, and a second one would only race the first. An absent or
   * unknown value, and a link that names the tab already open, leave the desk exactly as it is, so a
   * hand-typed `?scope=nonsense` is the default tab and not a blank page.
   */
  showScopeFromLink(raw: unknown) {
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (!isTicketScope(value) || value === this.scope) return;
    this.switchScope(value);
  }

  /**
   * Point the desk at another tab — and let go of the rows that answered the
   * last one.
   *
   * THE ROWS AND THE COUNT ARE PART OF THE SCOPE. Writing `scope` alone left
   * the previous tab's page and total in place under the new tab's label: the
   * header said "My team" while the body listed what was waiting on ME and
   * footed it "Showing the newest 12 of 57" from a scope nobody had asked
   * about yet. A successful read overwrites them a moment later, so this reads
   * as a flicker — but a read that FAILS never does, and the false list stands
   * under the wrong heading until the person changes tabs again.
   *
   * `loadedSearch` gets the same care in the other direction: it is
   * deliberately NOT advanced by a failed read, so `visible` keeps narrowing
   * the page it has. That works because those rows are still rows of this
   * scope, only possibly stale for the term. A scope's rows cannot be narrowed
   * into another scope's, so there is nothing to keep — they go, and the tab
   * reads as "still loading" until its own answer arrives.
   */
  private switchScope(scope: TicketScope) {
    this.scope = scope;
    this.forgetRows();
  }

  /**
   * Let go of the rows that answered another question. The scope's reason
   * (above) holds for the "Not picked up" switch as well, and more sharply:
   * it NARROWS the list, so a failed read that left the previous rows standing
   * under the switch would list tickets somebody has taken as if nobody had.
   */
  private forgetRows() {
    this.page = [];
    this.total = null;
    this.loaded = false;
    this.error = null;
    this.loadedSearch = "";
  }

  setIncludeClosed(on: boolean) {
    this.includeClosed = on;
    void this.load();
  }

  setAwaitingPickupOnly(on: boolean) {
    if (this.awaitingPickupOnly === on) return;
    this.awaitingPickupOnly = on;
    this.forgetRows();
    void this.load();
  }

  /**
   * Turn the "Not picked up" switch on when a link says so (`?pickup=waiting`,
   * read by the route's `onEnter`; the dashboards' count of such tickets links
   * here). Like `showScopeFromLink` it does NOT load, and a link without it, or
   * with a value it does not know, leaves the desk as it is.
   */
  showPickupFromLink(raw: unknown) {
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value !== "waiting" || this.awaitingPickupOnly) return;
    this.awaitingPickupOnly = true;
    this.forgetRows();
  }

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  /** Filters the page instantly, and asks the server after a pause so a match older than the page is reachable. */
  setSearchQuery(q: string) {
    this.searchQuery = q;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      void this.load();
    }, 350);
  }

  /**
   * The page as it should read. Once the server has answered for the current
   * term, the page IS that answer (it matches handles, names and emails the
   * rows do not carry). While a server read for a NEW term is still pending,
   * narrow the old page locally so typing feels instant.
   */
  get visible(): TicketRecord[] {
    const term = this.searchQuery.trim();
    // Exact comparison, not case-folded: the server decides what "Æ" and "æ"
    // mean, so a change of case is a new term until the server has answered it.
    if (!term || this.loadedSearch === term) return this.page;
    const q = term.toLowerCase();
    return this.page.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.body ?? "").toLowerCase().includes(q) ||
        `tkt-${t.id}`.includes(q) ||
        (t.requesterName ?? "").toLowerCase().includes(q) ||
        (t.assigneeName ?? "").toLowerCase().includes(q),
    );
  }

  async load() {
    // Disabled desk: see `wiringError`. Reading the list would be exactly the
    // thing there is no longer anything to clean up after.
    if (this.wiringError) return;
    const service = this.ticketsService;
    if (!service) return;
    // A different person than the one this desk was filled for: everything
    // held is theirs, not this one's.
    const who = this.currentUserId();
    if (this.loadedForUserId !== null && this.loadedForUserId !== who) this.reset();
    const seq = ++this.loadSeq;
    // The server gets the term as typed (its LIKE does its own folding —
    // lower-casing here would break non-ASCII matches on SQLite), and the
    // bookkeeping key is the same string, unnormalised.
    const search = this.searchQuery.trim();
    runInAction(() => {
      this.loading = true;
    });
    try {
      const res = (await service.find({
        query: {
          scope: this.scope,
          includeClosed: this.includeClosed,
          ...(this.awaitingPickupOnly ? { awaitingPickup: true } : {}),
          ...(search ? { search } : {}),
          $limit: 200,
          $sort: { updatedAt: -1 },
        },
      })) as unknown as
        | { data?: TicketRecord[]; total?: number }
        | TicketRecord[]
        | null
        | undefined;
      // Same test as every other answer in this store: the generation AND the
      // person. A list read that was in flight across a sign-out the reaction
      // could not see would otherwise paint the previous person's rows.
      if (seq !== this.loadSeq || this.currentUserId() !== who) return;
      runInAction(() => {
        if (!res) {
          // The library ServiceStore swallows transport errors and resolves
          // null. The page is unchanged, so it is NOT the answer for this term:
          // `loadedSearch` stays as it was and `visible` keeps narrowing.
          this.error = "The ticket list could not be loaded.";
          this.loaded = true;
        } else if (Array.isArray(res)) {
          this.page = res;
          this.total = res.length;
          this.error = null;
          this.loaded = true;
          this.loadedSearch = search;
          this.loadedForUserId = who;
        } else {
          this.page = res.data ?? [];
          this.total =
            typeof res.total === "number" ? res.total : this.page.length;
          this.error = null;
          this.loaded = true;
          this.loadedSearch = search;
          this.loadedForUserId = who;
        }
        // Keep the open ticket in step with the list.
        if (this.selected) {
          const fresh = this.page.find((t) => t.id === this.selected?.id);
          if (fresh) {
            this.selected = fresh;
            this.seedDueDraft(fresh);
          }
        }
      });
    } catch (err) {
      // Ended by a sign-out: not "loaded", and not a failure — the store was reset.
      if (seq !== this.loadSeq || this.currentUserId() !== who || isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          (err as Error)?.message || "The ticket list could not be loaded.";
        this.loaded = true;
      });
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Why the people picker is empty, when it is — a failed load, not "nobody". */
  usersError: string | null = null;
  /** A people read is in flight — the re-entrancy guard, now that failure no longer latches. */
  private usersLoading = false;

  /**
   * Everyone, for the person picker and the team list.
   *
   * TWO THINGS THIS DOES NOT DO, both of which it used to.
   *
   * IT DOES NOT LATCH ON FAILURE. `usersLoaded` was set in the `finally`,
   * so a single dropped request made the pickers empty for the rest of the
   * session: every later call — a second New ticket, a Reassign, the Refresh
   * button re-running onEnter — returned at the guard and issued nothing.
   * `usersLoaded` is now the record of an ANSWER, written only where one
   * arrived, and `usersLoading` is the re-entrancy guard that stops two
   * dialogs opening two requests. A failure leaves both false: the next
   * attempt retries, which is what a transient failure needs.
   *
   * IT DOES NOT WRITE FOR SOMEBODY ELSE. Every other read in this store
   * captures its generation and its person and re-tests both in each
   * continuation; this one wrote its flags unconditionally, so A's people read
   * landing after a sign-out re-latched `usersLoaded` for B — whose own
   * `loadUsers()` then returned at the guard and never issued a request,
   * leaving the picker showing nobody with `usersError` null and no spinner:
   * the false-empty this field exists to prevent. `reset()` advances
   * `usersSeq`, so a read that outlived its person now writes nothing at all.
   */
  async loadUsers() {
    if (this.usersLoaded || this.usersLoading) return;
    const seq = ++this.usersSeq;
    const who = this.currentUserId();
    runInAction(() => {
      this.usersLoading = true;
      this.usersError = null;
    });
    let failure: string | null = null;
    try {
      const res = await this.usersService?.find({ query: { $limit: 500 } });
      if (res == null) {
        // The ServiceStore swallowed it: the pickers would show nobody and the
        // form would read as "no one to ask". Name the failure instead.
        const store = this.usersService as unknown as {
          errorOnFind?: { message?: unknown } | null;
        } | null;
        const msg = store?.errorOnFind?.message;
        failure =
          typeof msg === "string" && msg
            ? msg
            : "The people list could not be loaded.";
      }
    } catch (err) {
      failure = (err as Error)?.message || "The people list could not be loaded.";
    } finally {
      // The generation AND the person, as everywhere else here: this read owns
      // these flags only while both still hold.
      if (seq === this.usersSeq && this.currentUserId() === who) {
        runInAction(() => {
          this.usersLoading = false;
          this.usersLoaded = failure === null;
          this.usersError = failure;
        });
      }
    }
  }

  // ---- one ticket ------------------------------------------------------------

  /**
   * Open a ticket in the drawer; fetches it if it is not on the current page
   * (a bell click-through). Guarded like the list: opening A then B while A's
   * read is still in flight must not let A's answer land in B's drawer.
   */
  async open(id: number) {
    if (this.wiringError) {
      runInAction(() => {
        this.actionError = this.wiringError;
      });
      return;
    }
    const seq = ++this.openSeq;
    // Whose drawer this is. `reset()` advances `openSeq`, so the generation
    // alone covers a sign-out the reaction SAW; this covers one it could not
    // (an auth object that changes without notifying MobX), where the desk
    // would otherwise still be filled and `seq` still current.
    const who = this.currentUserId();
    const onPage = this.page.find((t) => t.id === id) ?? null;
    runInAction(() => {
      this.selected = onPage;
      this.events = [];
      this.actionError = null;
      this.commentDraft = "";
      this.assignTo = null;
      // Seeded from the ticket, so pressing Set without touching the field
      // re-sends the date shown rather than clearing it. Clearing is done by
      // emptying the field, which sets this to null. A new drawer is a fresh
      // field: whatever the previous ticket's draft was, it is not this one's.
      this.dueDraftDirty = false;
      this.dueDraft = onPage?.dueAt ?? null;
    });
    if (!onPage) {
      try {
        const t = (await this.ticketsService?.get(id)) as unknown as
          | TicketRecord
          | null
          | undefined;
        runInAction(() => {
          if (seq !== this.openSeq || this.currentUserId() !== who) return;
          this.selected = t ?? null;
          this.dueDraftDirty = false;
          this.dueDraft = t?.dueAt ?? null;
          if (!t) this.actionError = this.missingTicketMessage(id);
        });
      } catch (err) {
        runInAction(() => {
          if (seq !== this.openSeq || this.currentUserId() !== who) return;
          if (!isSessionEnded(err)) {
            this.actionError =
              (err as Error)?.message || `Ticket TKT-${id} could not be loaded.`;
          }
        });
      }
    }
    // THE GENERATION AND A TICKET, and deliberately not the person again.
    // `reset()` and `close()` both advance `openSeq`, so a drawer that has
    // moved on — or been emptied for the next person — starts no timeline read
    // here; that matters, because such a read's spinner would be left up by
    // its own stale-generation `finally`. Re-checking the person would add
    // nothing AT THIS LINE: `selected` is non-null only because the block
    // above set it, that block refuses to set it for anyone but `who`, and
    // there is no suspension point between the two. Put an await in between
    // and that stops being true — check the person again if you do.
    if (seq === this.openSeq && this.selected) await this.loadEvents(id, seq);
  }

  close() {
    // A closed drawer is a new generation: a read or an action still in
    // flight for the ticket that was open must not reopen it.
    this.openSeq += 1;
    this.selected = null;
    this.events = [];
    this.actionError = null;
  }

  async loadEvents(ticketId: number, seq: number = this.openSeq) {
    const service = this.eventsService;
    if (!service) return;
    // Same reasoning as open(): a timeline is one person's reading of one
    // ticket, and must not land on the next person's desk.
    const who = this.currentUserId();
    // AND ITS OWN GENERATION, because `openSeq` cannot tell two reads of the
    // SAME drawer apart. `act()` reads the timeline again with the drawer's
    // seq while `open()`'s first read may still be in flight; whichever lands
    // last wins, and if that is the older one the drawer shows the timeline
    // from before the comment that was just added, then hides the spinner the
    // newer read is still raising.
    const gen = ++this.eventsSeq;
    runInAction(() => {
      this.eventsLoading = true;
      this.eventsError = null;
    });
    try {
      const res = (await service.find({ query: { ticketId } })) as unknown as
        | TicketEventRecord[]
        | { data?: TicketEventRecord[] }
        | null
        | undefined;
      runInAction(() => {
        if (gen !== this.eventsSeq) return;
        if (seq !== this.openSeq || this.currentUserId() !== who) return;
        if (!res) {
          // Swallowed by the ServiceStore: not an empty timeline, a failed read.
          const err = (
            service as unknown as { errorOnFind?: { message?: string } | null }
          ).errorOnFind;
          this.eventsError =
            err?.message || "The timeline could not be loaded.";
          return;
        }
        this.events = Array.isArray(res) ? res : (res?.data ?? []);
      });
    } catch (err) {
      runInAction(() => {
        if (gen !== this.eventsSeq) return;
        if (seq !== this.openSeq || this.currentUserId() !== who) return;
        if (!isSessionEnded(err)) {
          this.eventsError =
            (err as Error)?.message || "The timeline could not be loaded.";
        }
      });
    } finally {
      // The spinner belongs to the newest timeline read, in the drawer that
      // asked for it: an older sibling settling first must not take it down.
      runInAction(() => {
        if (gen === this.eventsSeq && seq === this.openSeq)
          this.eventsLoading = false;
      });
    }
  }

  /** The actions this person may take on the open ticket, from the shared rule. */
  get actions(): TicketAction[] {
    if (!this.selected) return [];
    return legalActions(this.me, this.selected);
  }

  setCommentDraft(v: string) {
    this.commentDraft = v;
  }
  setAssignTo(id: number | null) {
    this.assignTo = id;
  }
  setDueDraft(v: string | null) {
    this.dueDraft = v;
    // Theirs now: a later refresh re-seeds the field only while it is untouched.
    this.dueDraftDirty = true;
  }

  /**
   * Put the ticket's own deadline back in the field, because the ticket the
   * field belongs to was just replaced. A date the person has typed since the
   * field was seeded wins — they are mid-edit, and their entry is not the
   * server's to overwrite.
   */
  private seedDueDraft(t: TicketRecord | null) {
    if (this.dueDraftDirty) return;
    this.dueDraft = t?.dueAt ?? null;
  }

  /**
   * Take an action on the open ticket. `body` is the comment text or the
   * resolution note; the server writes the event and tells the right people.
   */
  async act(action: TicketAction, body?: string | null): Promise<boolean> {
    const t = this.selected;
    const service = this.ticketsService;
    if (this.wiringError || !t || !service || this.acting) return false;
    // WHAT THIS ANSWER MAY LAND IN, decided before the await. `reset()` (a
    // sign-out or account switch) and `close()` both advance `openSeq`, and
    // an answer that arrives after either must not put the previous person's
    // ticket back into the drawer, nor a closed one back on screen. The write
    // itself still happened on the server; only what is shown is discarded.
    const seq = this.openSeq;
    const who = this.currentUserId();
    // WHO OWNS THE BUTTON. `acting` is the re-entrancy guard — one action at a
    // time — and it is shared, so clearing it is a claim about the CURRENT
    // action, not about this one. A completion that no longer owns it re-enables
    // a button its successor's request is still in flight behind: the second
    // click files a second comment. `openSeq` cannot stand in for this (it is
    // unchanged by a sign-out the reaction could not see, and it moves when the
    // drawer merely closes, which would strand `acting` set forever).
    const token = ++this.actSeq;
    const payload: Record<string, unknown> = { action };
    const text = (
      body ?? (action === "comment" ? this.commentDraft : "")
    ).trim();
    if (text) payload.body = text;
    if (action === "assign") payload.assigneeId = this.assignTo;
    if (action === "set-due") payload.dueAt = this.dueDraft;
    runInAction(() => {
      this.acting = action;
      this.actionError = null;
    });
    try {
      const updated = (await service.patch(t.id, payload)) as unknown as
        | TicketRecord
        | null
        | undefined;
      const sameDrawer = seq === this.openSeq;
      const samePerson = this.currentUserId() === who;
      // BOTH, for every write. The generation alone is not enough: load() and
      // a sign-out the reaction could not see leave `openSeq` untouched, so
      // `sameDrawer` stays true across an account change and this answer would
      // paint the previous person's ticket on the next person's screen.
      const mine = sameDrawer && samePerson;
      if (!updated) {
        // Swallowed by the ServiceStore: the write did NOT happen.
        if (mine) {
          runInAction(() => {
            this.actionError = this.swallowed("Patch") ?? `${action} failed.`;
          });
        }
        return false;
      }
      if (mine) {
        runInAction(() => {
          this.selected = updated;
          // The deadline the server now holds is what the field must show —
          // and a set-due that just succeeded spends the edit that made it.
          if (action === "set-due") this.dueDraftDirty = false;
          this.seedDueDraft(updated);
          if (action === "comment") this.commentDraft = "";
          if (action === "assign") this.assignTo = null;
        });
      }
      // Another person's desk is not this action's to refresh. A drawer that
      // was closed (or moved to another ticket) still wants the list it came
      // from brought up to date — the acted row is stale there.
      if (!samePerson) return true;
      await Promise.all([
        mine ? this.loadEvents(t.id, seq) : Promise.resolve(),
        this.load(),
      ]);
      return true;
    } catch (err) {
      if (seq === this.openSeq && this.currentUserId() === who) {
        runInAction(() => {
          if (!isSessionEnded(err)) this.actionError = (err as Error)?.message || `${action} failed.`;
        });
      }
      return false;
    } finally {
      // Only while this action is still the one the button is disabled for.
      if (this.actSeq === token) {
        runInAction(() => {
          this.acting = null;
        });
      }
    }
  }

  // ---- filing ----------------------------------------------------------------

  openNewTicket(preset: Partial<TicketDraft> = {}) {
    this.draft = { ...emptyDraft(), ...preset };
    this.actionError = null;
    void this.loadUsers();
    void this.loadSubjects();
  }

  updateDraft(patch: Partial<TicketDraft>) {
    if (!this.draft) return;
    this.draft = { ...this.draft, ...patch };
  }

  /** What the dialog's submit is gated on: see `draftProblem`. */
  get draftValid(): boolean {
    return Boolean(this.draft) && this.draftProblem === null;
  }

  /**
   * Why the draft cannot be filed yet, in the dialog's words, or null.
   *
   * ENGINEERING WORK (a feedback kind) needs no one named: the API sends it to
   * the Engineering queue whoever else is asked. It does need its DETAILS:
   * the words a developer, or Gantry's planning pass, works from. Gantry will
   * not lift a ticket with no body (it refuses the row, and HQ never hears
   * why), so a title-only bug would wait on the queue for a person forever.
   * Every other kind is one person asking another, and must say whom.
   */
  get draftProblem(): string | null {
    const d = this.draft;
    if (!d) return null;
    if (isFeedbackKind(d.kind)) {
      return d.title.trim() && d.body.trim()
        ? null
        : "Engineering work needs a title and details: what is wanted, in the words of whoever asked for it. Gantry does not lift a ticket that has none.";
    }
    return d.title.trim() && (d.assigneeId != null || d.team)
      ? null
      : "A ticket needs a title and someone to ask — a person, a team, or both.";
  }

  /**
   * Where the draft will go when it is filed, said before it is filed — so a
   * client issue is not left on one person's desk by somebody who believed it
   * would reach engineering. Mirrors the API's routing (tickets.class.ts
   * `filingTeam`); the API decides.
   */
  get draftRouteNote(): { severity: "info" | "warning"; text: string } | null {
    const d = this.draft;
    if (!d) return null;
    if (isFeedbackKind(d.kind)) {
      if (d.team && !sameTeam(d.team, ENGINEERING_TEAM)) {
        return { severity: "info", text: `It goes to team ${d.team}'s queue rather than Engineering's.` };
      }
      // The people list is the desk's only view of who is on a team, and a
      // queue nobody is on is not where it goes: named nobody, the API refuses
      // it; named a person, it goes to them alone (tickets.class.ts).
      if (!this.teams.some((t) => sameTeam(t.team, ENGINEERING_TEAM))) {
        return {
          severity: "warning",
          text: "Nobody is on the Engineering team, so this reaches only the person you name. Ask an administrator to put the developers on Engineering.",
        };
      }
      const subject = d.clientId || d.governedSlug;
      return subject
        ? {
            severity: "info",
            text: "It goes to the Engineering queue, even if you also name a person, and Gantry can lift it into engineering work from there.",
          }
        : {
            severity: "warning",
            text: "It goes to the Engineering queue. Say which client or module it is about, or Gantry cannot place it and will decline it.",
          };
    }
    if (d.clientId || d.governedSlug) {
      const kind = KIND_LABEL[d.kind] ?? d.kind;
      return {
        severity: "warning",
        text: `${kind} tickets reach only the person or team you name; nothing in engineering reads them. If the client needs something built or fixed, choose Bug, Feature request or Feedback.`,
      };
    }
    return null;
  }

  /**
   * File the draft. By default the desk FOLLOWS what was filed: it switches to
   * "I asked", re-reads the list and opens the new ticket in the drawer. A
   * caller filing from somewhere else — Client 360's Tickets tab — passes
   * `{ follow: false }` to only create it (and clear the draft): changing the
   * desk's scope, list and selection behind the back of a person who is not
   * looking at the desk is the surprise this avoids.
   */
  async submitNewTicket(
    options: { follow?: boolean } = {},
  ): Promise<TicketRecord | null> {
    const follow = options.follow !== false;
    const d = this.draft;
    const service = this.ticketsService;
    // `submitting` is the re-entrancy guard: `draftValid` stays true right
    // through the await (the draft is cleared after it), and the library
    // dialog's footer awaits onSubmit with no pending state of its own — so
    // without this a double-click filed the ticket twice and paged the
    // assignee twice.
    if (this.wiringError || !d || !service || !this.draftValid || this.submitting)
      return null;
    // Who is filing it. If they are gone by the time it is filed, the ticket
    // still exists — but it is not the next person's to be shown.
    const who = this.currentUserId();
    // AND WHOSE SUBMISSION OWNS THE GATE. `submitting` and `draft` are shared:
    // clearing them says something about the submission happening NOW. A
    // completion that no longer owns them cleared the gate while its
    // successor's create was still in flight — the dialog's footer re-enabled,
    // and a second press filed a second ticket — and wiped the draft the
    // current person was in the middle of writing.
    const token = ++this.submitSeq;
    // `actionError` is the DRAWER's field (an open ticket's refused action;
    // the drawer opens on it when no ticket is selected), so a filing neither
    // clears nor sets it: a refusal is THROWN, and the dialog's footer shows
    // it once. Setting it here popped the drawer open behind the dialog with
    // the same message a third time.
    runInAction(() => {
      this.submitting = true;
    });
    try {
      const created = (await service.create({
        title: d.title.trim(),
        body: d.body.trim() || null,
        kind: d.kind,
        priority: d.priority,
        assigneeId: d.assigneeId,
        team: d.team,
        dueAt: d.dueAt,
        clientId: d.clientId,
        governedSlug: d.governedSlug,
      })) as unknown as TicketRecord | null | undefined;
      const samePerson = this.currentUserId() === who;
      const mine = samePerson && this.submitSeq === token;
      if (!created) {
        // Swallowed by the ServiceStore: nothing was filed. Keep the draft.
        // swallowedFailure keeps a sign-out's SessionEndedError as-is, so the
        // caller can still tell it from a refusal.
        throw swallowedFailure(this.ticketsService, "errorOnCreate", "The ticket could not be filed.");
      }
      // The draft this call filed, proved by identity — not whatever is in the
      // box now. `updateDraft` and `openNewTicket` both REPLACE the object, so
      // a draft that is no longer `d` is somebody else's writing.
      if (mine && this.draft === d) {
        runInAction(() => {
          this.draft = null;
        });
      }
      // Filed by someone who has since left this browser: it is theirs, and
      // repointing the desk at it would open one person's ticket on another
      // person's screen.
      if (!mine || !follow) return created;
      // A ticket I filed shows under "I asked"; switch there so it is visible.
      // In a `runInAction`: `makeAutoObservable` ends this method's action at
      // the first await, so a bare write here is a modification outside an
      // action — MobX's strict mode warns on it, and this repo runs a
      // zero-warning policy.
      if (this.scope !== "asked" && this.scope !== "all")
        runInAction(() => {
          this.switchScope("asked");
        });
      await this.load();
      await this.open(created.id);
      return created;
    } catch (err) {
      if (err instanceof Error && !err.message) {
        throw new Error("The ticket could not be filed.");
      }
      throw err;
    } finally {
      // Only while this submission is still the one the gate is closed for.
      if (this.submitSeq === token) {
        runInAction(() => {
          this.submitting = false;
        });
      }
    }
  }
}

export default TicketDeskStore;
